import React from 'react';
import {
  SettingsIcon,
  AtSignIcon,
  CalendarIcon,
  ViewIcon,
  StarIcon,
  InfoIcon,
  BellIcon,
} from '@chakra-ui/icons';
import { FaDatabase } from 'react-icons/fa';
import { getKeycloakAdminUrl } from '../../utils/keycloakAdminUrl';

export type SidebarNode = {
  id: string;
  label: string;
  icon: React.ElementType;
  to?: string;
  children?: SidebarNode[];
  isCompleted?: boolean;
  isCurrent?: boolean;
};

export type SidebarNodeDef = {
  id: string;
  labelKey: string;
  icon: React.ElementType;
  children?: SidebarNodeDef[];
};

// Base navigation structure
export const BASE_NAV_DEFS: SidebarNodeDef[] = [
  { id: 'classrooms', labelKey: 'nav.classrooms', icon: CalendarIcon },
  { id: 'exercise-library', labelKey: 'nav.exerciseLibrary', icon: StarIcon },
  { id: 'public-resources', labelKey: 'nav.public-resources', icon: ViewIcon },
  { id: 'indexed-documents', labelKey: 'nav.indexedDocuments', icon: ViewIcon },
  { id: 'students', labelKey: 'nav.students', icon: AtSignIcon },
  {
    id: 'admin',
    labelKey: 'nav.admin',
    icon: SettingsIcon,
    children: [
      { id: 'admin.users', labelKey: 'nav.admin.users', icon: AtSignIcon },
      { id: 'admin.system', labelKey: 'nav.admin.system', icon: SettingsIcon },
      { id: 'admin.aiConfig', labelKey: 'nav.admin.aiConfig', icon: SettingsIcon },
      { id: 'admin.materializedViews', labelKey: 'nav.admin.materializedViews', icon: FaDatabase },
      { id: 'admin.ttsAudioCache', labelKey: 'nav.admin.ttsAudioCache', icon: FaDatabase },
      { id: 'admin.auditLogs', labelKey: 'nav.admin.auditLogs', icon: InfoIcon },
      { id: 'admin.errorLogs', labelKey: 'nav.admin.errorLogs', icon: BellIcon },
      { id: 'admin.feTelemetryLogs', labelKey: 'nav.admin.feTelemetryLogs', icon: BellIcon },
    ],
  },
];

// Student-specific navigation (classrooms only for now)
export const STUDENT_NAV_DEFS: SidebarNodeDef[] = [
  { id: 'my-classrooms', labelKey: 'nav.my-classrooms', icon: CalendarIcon },
  { id: 'public-resources', labelKey: 'nav.public-resources', icon: ViewIcon },
];

// Get navigation based on role
export function getNavDefs(role: string): SidebarNodeDef[] {
  if (role === 'STUDENT') {
    return STUDENT_NAV_DEFS;
  }
  if (role === 'ADMIN') {
    return BASE_NAV_DEFS;
  }
  // For TEACHER, show all except admin menu and students menu
  return BASE_NAV_DEFS.filter((item) => item.id !== 'admin' && item.id !== 'students');
}

// Map node id -> route path (can be internal or external URL)
export function resolveNavPath(id: string): string | undefined {
  switch (id) {
    case 'classrooms':
      return '/classrooms';
    case 'my-classrooms':
      return '/my-classrooms';
    case 'exercise-library':
      return '/exercise-library';
    case 'public-resources':
      return '/public-resources';
    case 'indexed-documents':
      return '/indexed-documents';
    case 'students':
      return '/students';
    case 'students.list':
      return '/students';
    case 'students.progress':
      return '/students/progress';
    case 'admin.users':
      return getKeycloakAdminUrl('/users');
    case 'admin.roles':
      return '/admin/roles';
    case 'admin.system':
      return '/admin/system';
    case 'admin.aiConfig':
      return '/admin/ai-config';
    case 'admin.materializedViews':
      return '/admin/materialized-views';
    case 'admin.ttsAudioCache':
      return '/admin/tts-audio-cache';
    case 'admin.auditLogs':
      return '/admin/audit-logs';
    case 'admin.errorLogs':
      return '/admin/error-logs';
    case 'admin.feTelemetryLogs':
      return '/admin/fe-telemetry-logs';
    case 'settings.preferences':
      return '/settings';
    default:
      return undefined;
  }
}
